import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MarkdownService } from 'ngx-markdown';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as _ from 'lodash';

import { WikiService } from '../wiki/services/wiki.service';
import { WikiConflictService } from '../wiki/services/wiki.conflict.service';
import { UiService } from '../../core/services/core.ui.service';
import { GfTranslateService } from '../../core/services/core.translate.service';
import { FileTypeService } from '../../plugins/tracker/services/file-type.service';
import { MarkdownEditorService } from '../../core/services/core.markdown-editor.service';
import { escapeHtml } from '../../core/services/core.utils.service';
import { NotebookHost } from './notebook-host';
import { WikiRenameModalComponent } from '../wiki/wiki-rename-modal/wiki.rename.modal.component';
import { marker } from '../../core/services/ngx-translate-extract-marker';

/**
 * Shared, multi-page notebook UI backed by the wiki service. The host adapter
 * supplies the surface-specific behavior (data source, attachments, lifecycle
 * hooks, feature flags); see {@link NotebookHost}.
 *
 * Used by the tracker item Notebook tab (<ti-notebook>) and the user notebook
 * (<user-notebook>), which each render <gf-notebook [host]="..."> once their
 * host context is ready.
 */
@Component({
    selector: 'gf-notebook',
    template: require('./gf-notebook.component.html')
})
export class GfNotebookComponent implements OnInit, OnDestroy {
    @Input() host!: NotebookHost;

    @ViewChild('fileInput', { static: false }) fileInputRef!: ElementRef;
    @ViewChild('wikiLinksEl', { static: false }) wikiLinksElRef?: ElementRef;
    @ViewChild('pageMarkdownEl', { read: ElementRef, static: false }) pageMarkdownElRef?: ElementRef;
    @ViewChild('fileMarkdownEl', { read: ElementRef, static: false }) fileMarkdownElRef?: ElementRef;

    pages: any[] = [];
    selectedPage: any = null;
    selectedContent: string = '';
    /** Version of {@link selectedPage} currently loaded in the editor, sent as `baseVersion` on
     *  save so a page someone else edited meanwhile is rejected instead of silently overwritten.
     */
    selectedPageVersion: number = 0;
    /** Rendered, sanitized HTML for the current page (view mode). */
    displaySafeHtml: SafeHtml = '';
    isEditing: boolean = false;
    isCreating: boolean = false;
    newPageName: string = '';
    loading: boolean = false;
    saving: boolean = false;
    togglingVisibility: boolean = false;

    selectedFile: any = null;
    selectedFileContent: string = '';
    isLoadingFile: boolean = false;
    files: any[] = [];
    filesUploading: any[] = [];
    confirmingDeletePage: boolean = false;
    /** Inline rename field state for the selected page. */
    isRenamingPage: boolean = false;
    renamePageName: string = '';
    confirmingDeleteFile: boolean = false;
    isViewingVersions: boolean = false;

    // ── markdown guide help sidebar ──
    showHelpPanel: boolean = false;
    helpSidebarTab: string = 'guide';
    helpSidebarWidth: number = 420;
    private readonly helpSidebarMinWidth: number = 320;
    private readonly helpSidebarMaxWidth: number = 640;
    private readonly helpSidebarWidthStorageKey: string = 'wiki.help.sidebar.width';
    private removeResizeListeners: (() => void) | null = null;

    constructor(
        private wikiService: WikiService,
        private uiService: UiService,
        private translateService: GfTranslateService,
        private fileTypeService: FileTypeService,
        private http: HttpClient,
        private markdownService: MarkdownService,
        private sanitizer: DomSanitizer,
        private markdownEditorService: MarkdownEditorService,
        private wikiConflictService: WikiConflictService,
        private modalService: NgbModal
    ) {}

    ngOnInit(): void {
        this.restoreHelpSidebarWidth();
        this.loadPages();
    }

    ngOnDestroy(): void {
        this.stopHelpSidebarResize();
    }

    // ─────────────────── convenience accessors ────────────────────

    get canUpdate(): boolean { return !!(this.host && this.host.canUpdate); }
    get section(): string { return this.host.section; }
    get refId(): number { return this.host.refId; }

    /** Whether files can currently be attached. Per-page hosts need a saved page. */
    get canAttach(): boolean {
        if (!this.canUpdate) { return false; }
        if (!this.host.attachmentsPerPage) { return true; }
        return !!(this.selectedPage && !this.selectedPage.isDraft && this.selectedPage.id);
    }

    // ─────────────────── pages ────────────────────

    private loadPages(): void {
        this.loading = true;
        this.wikiService.fetch(this.host.pageListParams())
            .then((response: any) => {
                this.pages = Array.isArray(response) ? response : _.get(response, 'items', []);
                this.loading = false;
                if (this.host.onChange) { this.host.onChange(this.pages); }
                // Load attachments up front. For host-level (e.g. tracker item) attachments
                // this surfaces files even when there are no pages yet; for per-page hosts
                // with no selection it simply clears the list (selectPage reloads below).
                this.refreshAttachments();
                if (this.pages.length > 0 && !this.selectedPage) {
                    this.selectPage(this.initialPage());
                } else {
                    this.updateDisplayContent();
                }
            })
            .catch(() => {
                this.loading = false;
            });
    }

    /**
     * Which page to land on after the initial fetch. Honors a deep-linked
     * `host.initialPagename` (e.g. from the URL) when it matches a fetched page; falls back to
     * the first page (as before) otherwise, warning if a pagename was requested but not found.
     */
    private initialPage(): any {
        const wanted = this.host.initialPagename;
        if (!wanted) { return this.pages[0]; }
        const match = _.find(this.pages, (p: any) => p.pagename === wanted);
        if (match) { return match; }
        this.warn(marker('That notebook page could not be found'));
        return this.pages[0];
    }

    /** Assign the selected page and let the host reflect the change (e.g. sync the URL). */
    private setSelectedPage(page: any): void {
        this.selectedPage = page;
        if (this.host.onPageSelected) { this.host.onPageSelected(page); }
    }

    selectPage(page: any): void {
        this.selectedFile = null;
        this.selectedFileContent = '';
        this.confirmingDeletePage = false;
        this.confirmingDeleteFile = false;
        this.setSelectedPage(page);
        this.selectedContent = '';
        this.selectedPageVersion = 0;
        this.displaySafeHtml = '';
        this.isEditing = false;
        this.isCreating = false;
        this.isViewingVersions = false;
        this.refreshAttachments();
        if (page.isDraft) { return; }
        this.loading = true;

        this.wikiService.page(page.id, { editable: true })
            .then((response: any) => {
                this.selectedContent = response.editableContent || '';
                this.selectedPageVersion = response.version || 0;
                this.updateDisplayContent();
                this.loading = false;
            })
            .catch(() => {
                this.loading = false;
                this.warn(marker('Could not load page content'));
            });
    }

    startCreate(): void {
        const draft = this.makeDraft('New Page');
        this.pages = [...this.pages, draft];
        this.setSelectedPage(draft);
        this.selectedFile = null;
        this.selectedFileContent = '';
        this.files = [];
        this.confirmingDeletePage = false;
        this.confirmingDeleteFile = false;
        this.isCreating = true;
        this.isEditing = false;
        this.newPageName = 'New Page';
    }

    cancelCreate(): void {
        this.pages = this.pages.filter((p: any) => !p.isDraft);
        this.setSelectedPage(null);
        this.isCreating = false;
        this.newPageName = '';
    }

    startEdit(): void {
        if (!this.selectedPage) { return; }
        this.isEditing = true;
    }

    cancelEdit(): void {
        this.isEditing = false;
    }

    savePage(content: string): void {
        if (this.isCreating) {
            const name = this.newPageName.trim();
            if (!name) {
                this.warn(marker('Please enter a page name'));
                return;
            }
            this.saving = true;
            this.wikiService.createForSection(this.section, this.refId, name, content)
                .then((page: any) => {
                    this.saving = false;
                    this.isCreating = false;
                    this.newPageName = '';
                    this.pages = [...this.pages.filter((p: any) => !p.isDraft), page];
                    this.setSelectedPage(page);
                    this.selectedContent = content;
                    this.selectedPageVersion = _.get(page, 'latestVersion', 0);
                    this.updateDisplayContent();
                    this.refreshAttachments();
                    this.isEditing = false;
                    if (this.host.onChange) { this.host.onChange(this.pages); }
                })
                .catch((error: any) => {
                    this.saving = false;
                    // A create over a name that is already taken is refused rather than
                    // overwriting it; that reason is worth showing, since choosing another
                    // name is the way out of it.
                    this.warn(this.wikiConflictService.parseRejectionMessage(error)
                        || marker('Could not create notebook page'));
                });
            return;
        }

        if (!this.selectedPage) { return; }
        this.saving = true;
        const pageId = this.selectedPage.id;
        const payload: any = { content, content_type: 'text/markdown' };
        // Required by the server on an update. Guarded only so a page whose content load failed
        // produces the clearer "required" error rather than a "must be a positive integer" one.
        if (this.selectedPageVersion > 0) { payload.baseVersion = this.selectedPageVersion; }

        this.wikiService.update(pageId, payload)
            .catch((error: any) => this.resolveSaveConflict(error, pageId, payload))
            .then((response: any) => {
                this.saving = false;
                this.isEditing = false;
                this.selectedContent = content;
                // Move our base forward, or the next save in this session would conflict with
                // the one we just made.
                this.selectedPageVersion = _.get(response, 'latestVersion', 0) || this.selectedPageVersion;
                this.updateDisplayContent();
                this.uiService.message.showSuccess(
                    this.translateService.translate('Page saved'),
                    this.translateService.translate('Notebook')
                );
                if (this.host.onChange) { this.host.onChange(this.pages); }
            })
            .catch((error: any) => {
                this.saving = false;
                // On a conflict the user declined to overwrite, stay in the editor with their
                // text intact
                if (error !== GfNotebookComponent.CONFLICT_UNRESOLVED) {
                    this.warn(marker('Could not save notebook page'));
                }
            });
    }

    /**
     * Rejection marker for "the save conflicted and the user chose to keep editing".
     * Distinguishes that from a genuine save failure, which gets an error message.
     */
    private static readonly CONFLICT_UNRESOLVED = 'notebook-conflict-unresolved';

    /**
     * Turn a 409 from a page save into either a retry (the user chose to overwrite) or a
     * {@link CONFLICT_UNRESOLVED} rejection. Any other error is passed through untouched.
     */
    private resolveSaveConflict(error: any, pageId: number, payload: any): Promise<any> {
        const conflict = this.wikiConflictService.parse(error);
        if (!conflict) { return Promise.reject(error); }

        return this.wikiConflictService.open(conflict, pageId).then((choice) => {
            if (choice !== 'overwrite') {
                return Promise.reject(GfNotebookComponent.CONFLICT_UNRESOLVED);
            }
            return this.wikiService.update(pageId, { ...payload, baseVersion: conflict.currentVersion });
        });
    }

    promptDeletePage(): void { this.confirmingDeletePage = true; }
    cancelDeletePage(): void { this.confirmingDeletePage = false; }

    // ─────────────────────────── rename ────────────────────────────

    startRenamePage(): void {
        // The button is already gated on canUpdate; re-checked here so the field cannot be
        // opened by any other route.
        if (!this.canUpdate || !this.selectedPage || this.selectedPage.isDraft) { return; }
        this.renamePageName = this.selectedPage.pagename;
        this.isRenamingPage = true;
    }

    cancelRenamePage(): void {
        this.isRenamingPage = false;
        this.renamePageName = '';
    }

    get renameNameChanged(): boolean {
        const trimmed = (this.renamePageName || '').trim();
        return trimmed.length > 0 && trimmed !== _.get(this.selectedPage, 'pagename', '');
    }

    /**
     * Confirm and apply a rename.
     *
     * Goes through the shared rename dialog so the notebook gets the same impact report and
     * the same validation feedback (name already taken, illegal characters) as the project
     * wiki. Notebook pages are not addressable by a project wiki URL, so no project is passed
     * and the dialog lists referrers as plain names.
     */
    confirmRenamePage(): void {
        if (!this.canUpdate || !this.renameNameChanged) { return; }

        const page = this.selectedPage;
        const modalRef = this.modalService.open(WikiRenameModalComponent);
        modalRef.componentInstance.wiki = page;
        modalRef.componentInstance.project = null;
        modalRef.componentInstance.newPagename = (this.renamePageName || '').trim();
        modalRef.componentInstance.onRenamed = (report: any) => {
            const renamed = (report && report.pagename) || (this.renamePageName || '').trim();
            page.pagename = renamed;
            const listed = _.find(this.pages, { id: page.id });
            if (listed) { listed.pagename = renamed; }
            this.isRenamingPage = false;
            this.renamePageName = '';
            // Wiki links are resolved against the loaded page list by name, so re-render to
            // turn any link to the new name from a red "create this page" stub into a live one.
            this.updateDisplayContent();
            // The rename mutates pagename in place rather than reassigning selectedPage, so the
            // URL sync hook needs its own call here.
            if (this.host.onPageSelected) { this.host.onPageSelected(page); }
            if (this.host.onChange) { this.host.onChange(this.pages); }
        };
        modalRef.componentInstance.onClose = () => modalRef.close();
    }

    deletePage(page: any): void {
        this.confirmingDeletePage = false;
        this.wikiService.deleteWiki(page.id)
            .then(() => {
                this.pages = this.pages.filter((p: any) => p.id !== page.id);
                if (this.selectedPage && this.selectedPage.id === page.id) {
                    const next = this.pages.length > 0 ? this.pages[0] : null;
                    if (next) {
                        this.selectPage(next);
                    } else {
                        this.setSelectedPage(null);
                        this.selectedContent = '';
                        this.updateDisplayContent();
                        this.isEditing = false;
                    }
                }
                if (this.host.onChange) { this.host.onChange(this.pages); }
            })
            .catch(() => {
                this.warn(marker('Could not delete notebook page'));
            });
    }

    private makeDraft(name: string): any {
        return {
            id: null, pagename: name, isDraft: true, createDate: null,
            latestVersion: { pageVersion: 1, createDate: new Date().toISOString() }
        };
    }

    // ─────────────────── visibility / public links ────────────────────

    toggleVisibility(): void {
        if (!this.selectedPage || this.selectedPage.isDraft) { return; }
        const makePublic = !this.selectedPage.isPublic;
        this.togglingVisibility = true;
        this.wikiService.postVisibility(this.selectedPage.id, makePublic)
            .then((updated: any) => {
                this.togglingVisibility = false;
                this.selectedPage.isPublic = _.get(updated, 'isPublic', makePublic);
                const listed = _.find(this.pages, { id: this.selectedPage.id });
                if (listed) { listed.isPublic = this.selectedPage.isPublic; }
                this.uiService.message.showSuccess(
                    this.selectedPage.isPublic
                        ? this.translateService.translate('Page is now public')
                        : this.translateService.translate('Page is now private'),
                    this.translateService.translate('Notebook')
                );
            })
            .catch(() => {
                this.togglingVisibility = false;
                this.warn(marker('Could not change page visibility'));
            });
    }

    publicLink(page: any): string {
        return (this.host.publicPageHref && this.host.publicPageHref(page)) || '';
    }

    copyPublicLink(page: any): void {
        const link = this.publicLink(page);
        if (!link) { return; }
        const done = () => this.uiService.message.showSuccess(
            this.translateService.translate('Link copied to clipboard'),
            this.translateService.translate('Notebook')
        );
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(link).then(done).catch(() => {});
        }
    }

    // ─────────────────── versions ────────────────────

    showVersions(): void {
        this.isViewingVersions = true;
        this.isEditing = false;
        this.isCreating = false;
    }

    closeVersions(): void {
        this.isViewingVersions = false;
    }

    onVersionsRestored(): void {
        this.isViewingVersions = false;
        const page = this.selectedPage;
        if (page) { this.selectPage(page); }
        this.loadPages();
        if (this.host.onVersionsRestored) { this.host.onVersionsRestored(page); }
    }

    // ─────────────────── attachments ────────────────────

    triggerFileUpload(): void {
        this.fileInputRef.nativeElement.click();
    }

    private refreshAttachments(): void {
        if (this.host.attachmentsPerPage && (!this.selectedPage || this.selectedPage.isDraft || !this.selectedPage.id)) {
            this.files = [];
            return;
        }
        this.host.listAttachments(this.selectedPage)
            .then((list: any[]) => { this.files = Array.isArray(list) ? list : _.get(list, 'items', []); })
            .catch(() => { this.files = []; });
    }

    selectFile(file: any): void {
        this.selectedFile = file;
        this.isEditing = false;
        this.isCreating = false;
        this.isViewingVersions = false;
        this.selectedFileContent = '';
        this.isLoadingFile = false;
        this.confirmingDeletePage = false;
        this.confirmingDeleteFile = false;

        if (this.isMarkdown(file)) {
            this.isLoadingFile = true;
            this.http.get(file.attachmentUrl, { responseType: 'text' })
                .toPromise()
                .then((content: string) => {
                    this.selectedFileContent = content;
                    this.isLoadingFile = false;
                })
                .catch(() => {
                    this.isLoadingFile = false;
                    this.warn(marker('Could not load file content'));
                });
        }
    }

    onFileSelected(event: any): void {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) { return; }
        if (!this.canAttach) {
            event.target.value = '';
            this.warn(marker('Select or save a page before adding files'));
            return;
        }
        const page = this.selectedPage;

        Array.from(files).forEach((file: File) => {
            this.filesUploading.push({
                name: file.name, size: file.size,
                percentComplete: 0, amountComplete: 0, amountTotal: file.size
            });
        });

        const callbacks = {
            progressCallback: this.onUploadProgress.bind(this),
            finishedCallback: this.onUploadFinished.bind(this)
        };

        this.host.uploadAttachments(page, Array.from(files) as File[], callbacks)
            .then(() => {
                event.target.value = '';
                if (this.selectedPage === page) { this.refreshAttachments(); }
            })
            .catch(() => {
                this.filesUploading = [];
                event.target.value = '';
                this.warn(marker('Could not upload file'));
            });
    }

    private onUploadProgress(_section: string, _refId: number, file: any, bytesUploaded: number, bytesRemaining: number, percentUploaded: number): void {
        const entry = _.find(this.filesUploading, { name: file.name });
        if (entry) {
            entry.amountComplete = bytesUploaded;
            entry.amountTotal = bytesUploaded + bytesRemaining;
            entry.percentComplete = percentUploaded;
        }
    }

    private onUploadFinished(_section: string, _refId: number, file: any): void {
        _.remove(this.filesUploading, (f: any) => f.name === file.name);
    }

    promptDeleteFile(): void { this.confirmingDeleteFile = true; }
    cancelDeleteFile(): void { this.confirmingDeleteFile = false; }

    deleteFile(file: any): void {
        this.confirmingDeleteFile = false;
        this.host.deleteAttachment(this.selectedPage, file)
            .then(() => {
                this.files = this.files.filter((f: any) => f.id !== file.id);
                this.selectedFile = null;
                this.selectedFileContent = '';
            })
            .catch(() => {
                this.warn(marker('Could not delete file'));
            });
    }

    isMarkdown(file: any): boolean { return this.fileTypeService.isMarkdown(file); }
    isHtml(file: any): boolean { return this.fileTypeService.isHtml(file); }
    isImage(file: any): boolean { return this.fileTypeService.isImage(file); }
    isVideo(file: any): boolean { return this.fileTypeService.isVideo(file); }
    isAudio(file: any): boolean { return this.fileTypeService.isAudio(file); }
    isRenderable(file: any): boolean { return this.fileTypeService.isRenderable(file); }
    formatFileSize(bytes: number): string { return this.fileTypeService.formatFileSize(bytes); }

    // ─────────────────── view rendering ────────────────────

    /**
     * Recompute the rendered HTML for the current page. Only wiki-link surfaces use this
     * path (linkified [[Page]] anchors via [innerHTML]); other surfaces render the markdown
     * directly through the <markdown> component, so there is nothing to precompute.
     */
    private updateDisplayContent(): void {
        if (!this.host.enableWikiLinks) { return; }
        const md = this.selectedContent || '';
        if (!md) { this.displaySafeHtml = ''; return; }
        let html: string;
        try {
            html = this.markdownService.parse(md) as string;
        } catch (e) {
            html = md;
        }
        html = this.linkifyWikiWordsInHtml(html);
        this.displaySafeHtml = this.sanitizer.bypassSecurityTrustHtml(html);
        // [innerHTML] bindings update on the next change-detection pass, not
        // synchronously — defer a tick so the DOM actually holds the new content
        // before we try to enhance it.
        setTimeout(() => this.markdownEditorService.enhanceStaticHtml(this.wikiLinksElRef?.nativeElement), 0);
    }

    /** ngx-markdown has finished rendering the selected page's content into the DOM. */
    onPageMarkdownReady(): void {
        this.markdownEditorService.enhanceStaticHtml(this.pageMarkdownElRef?.nativeElement);
    }

    /** ngx-markdown has finished rendering the selected attached file's content into the DOM. */
    onFileMarkdownReady(): void {
        this.markdownEditorService.enhanceStaticHtml(this.fileMarkdownElRef?.nativeElement);
    }

    onWikiLinkClick(event: any): void {
        if (!this.host.enableWikiLinks) { return; }
        const el = event && event.target ? event.target as HTMLElement : null;
        const anchor = el && el.closest ? el.closest('a.notebook-wikilink') : null;
        if (!anchor) { return; }
        event.preventDefault();
        const name = anchor.getAttribute('data-notebook-page');
        if (name) { this.openOrCreateNotebookPage(name); }
    }

    private openOrCreateNotebookPage(name: string): void {
        const target = String(name).trim();
        if (!target) { return; }
        const existing = _.find(this.pages,
            (p: any) => !p.isDraft && p.pagename && String(p.pagename).trim() === target);
        if (existing) { this.selectPage(existing); return; }

        const draft = this.makeDraft(target);
        this.pages = [...this.pages.filter((p: any) => !p.isDraft), draft];
        this.selectedFile = null;
        this.selectedFileContent = '';
        this.confirmingDeletePage = false;
        this.confirmingDeleteFile = false;
        this.isViewingVersions = false;
        this.setSelectedPage(draft);
        this.selectedContent = '';
        this.isCreating = true;
        this.isEditing = false;
        this.newPageName = target;
    }

    private notebookPageExists(name: string): boolean {
        return _.some(this.pages,
            (p: any) => !p.isDraft && p.pagename && String(p.pagename).trim() === name);
    }

    private linkifyWikiWordsInHtml(html: string): string {
        const protectedRe = /<pre[\s\S]*?<\/pre>|<code[\s\S]*?<\/code>|<a\b[\s\S]*?<\/a>/gi;
        let out = '';
        let last = 0;
        let m: RegExpExecArray | null;
        while ((m = protectedRe.exec(html)) !== null) {
            out += this.replaceWikiTokens(html.slice(last, m.index));
            out += m[0];
            last = protectedRe.lastIndex;
        }
        out += this.replaceWikiTokens(html.slice(last));
        return out;
    }

    private replaceWikiTokens(text: string): string {
        text = text.replace(/\[\[([^\[\]|]+?)(?:\|([^\[\]]+?))?\]\]/g,
            (_m: string, page: string, disp: string) => this.wikiAnchor(page, disp));
        text = text.replace(/(!?)\[([^\[\]|]+?)(?:\|([^\[\]]+?))?\](?!\()/g,
            (m: string, bang: string, page: string, disp: string) => {
                if (bang) { return m; }
                if (/^https?:\/\//i.test(page.trim())) { return m; }
                return this.wikiAnchor(page, disp);
            });
        return text;
    }

    private wikiAnchor(page: string, disp: string): string {
        const name = (page || '').trim();
        if (!name) { return ''; }
        const label = escapeHtml(((disp || page) || '').trim());
        const dataAttr = escapeHtml(name);
        if (this.notebookPageExists(name)) {
            return `<a href="#" class="notebook-wikilink" data-notebook-page="${dataAttr}" style="cursor:pointer;">${label}</a>`;
        }
        return `<a href="#" class="notebook-wikilink notebook-wikilink-missing" data-notebook-page="${dataAttr}"`
            + ` title="Create this page" style="cursor:pointer;color:#b94a48;border-bottom:1px dashed #b94a48;text-decoration:none;">`
            + `${label}<sup>?</sup></a>`;
    }

    // ─────────────────── markdown guide sidebar ────────────────────

    toggleHelpPanel(): void {
        this.showHelpPanel = !this.showHelpPanel;
    }

    startHelpSidebarResize(event: MouseEvent): void {
        if (window.innerWidth < 992) { return; }
        event.preventDefault();

        const onMouseMove = (moveEvent: MouseEvent) => {
            const viewportWidth = window.innerWidth;
            const desiredWidth = viewportWidth - moveEvent.clientX;
            const maxFromViewport = Math.max(this.helpSidebarMinWidth, Math.floor(viewportWidth * 0.7));
            this.helpSidebarWidth = Math.max(
                this.helpSidebarMinWidth,
                Math.min(desiredWidth, Math.min(this.helpSidebarMaxWidth, maxFromViewport))
            );
        };

        const onMouseUp = () => {
            this.stopHelpSidebarResize();
            this.persistHelpSidebarWidth();
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        this.removeResizeListeners = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }

    private stopHelpSidebarResize(): void {
        if (this.removeResizeListeners) {
            this.removeResizeListeners();
            this.removeResizeListeners = null;
        }
    }

    private restoreHelpSidebarWidth(): void {
        const parsed = parseInt(window.localStorage.getItem(this.helpSidebarWidthStorageKey) || '', 10);
        if (!isNaN(parsed)) {
            this.helpSidebarWidth = Math.max(this.helpSidebarMinWidth, Math.min(parsed, this.helpSidebarMaxWidth));
        }
    }

    private persistHelpSidebarWidth(): void {
        window.localStorage.setItem(this.helpSidebarWidthStorageKey, String(this.helpSidebarWidth));
    }

    // ─────────────────── helpers ────────────────────

    private warn(message: string): void {
        this.uiService.message.showWarning(
            this.translateService.translate(message),
            this.translateService.translate('Notebook')
        );
    }
}
